import React, { useRef, useState } from "react";
import { message } from "antd";
import { formatQuestActivePeriod } from "common/questManagement.utils";
import { getPublicAssetUrl } from "components/productSellerCompanyCard/productSellerCompanyCard.utils";
import {
  CompanyQuestStatus,
  QuestRewardBadge,
  QuestWithProgress,
  useClaimQuestMutation,
  useGetMyQuestsQuery,
} from "reducers/questApi";
import {
  CompanyProfileCard,
  CompanyProfileCardTitle,
} from "./CompanyProfileCard";
import { fireConfettiAt } from "./questReward";

interface CardQuest {
  id: string;
  title: string;
  description: string;
  xp: number;
  /** Progress percentage, 0-100. */
  progress: number;
  status: CompanyQuestStatus;
  companyQuestId: string | null;
  rewardBadge: QuestRewardBadge | null;
  activePeriodLabel: string;
}

// gradation 2 — fill thanh tiến độ (lime → cyan)
const PROGRESS_GRADIENT = "bg-[linear-gradient(90deg,#d4f754_0%,#2cdcec_100%)]";

// gradation 4 — nền nút claim khi đã đạt 100% (claimable)
const CLAIM_GRADIENT =
  "bg-[linear-gradient(135deg,#d4f754_0%,#f4ffcd_30%,#d4f754_100%)]";

const clampProgress = (value: number): number =>
  Math.max(0, Math.min(100, value));

const toCardQuest = (q: QuestWithProgress): CardQuest => ({
  id: q.id,
  title: q.title,
  description: q.description ?? "",
  xp: q.reward_xp,
  progress: clampProgress(
    q.target_count > 0
      ? Math.round((q.progress.value / q.target_count) * 100)
      : 0,
  ),
  status: q.progress.status,
  companyQuestId: q.progress.company_quest_id,
  rewardBadge: q.reward_badge,
  activePeriodLabel: formatQuestActivePeriod(q),
});

// Huy chương phần thưởng: luôn dùng ảnh "unlocked" tĩnh từ public FE,
// fallback về vòng tròn rỗng khi quest không gắn badge.
const QuestRewardMedal = ({ badge }: { badge: QuestRewardBadge | null }) => {
  if (!badge) {
    return (
      <div className="h-[50px] w-[50px] shrink-0 rounded-full border border-white/30 bg-white/15" />
    );
  }

  return (
    <img
      src={getPublicAssetUrl(badge.icon_url)}
      alt={badge.name}
      title={badge.name}
      className="h-[50px] w-[50px] shrink-0 object-contain"
    />
  );
};

// gradation 1 — chữ EXP gradient (dùng cho cả hàng tiêu đề và hàng tiến độ)
const QuestXp = ({ xp, size }: { xp: number; size: 14 | 16 }) => (
  <span
    className={`shrink-0 bg-[linear-gradient(90deg,#d4f754_0%,#2cdcec_75%,#ff4a84_100%)] bg-clip-text font-['Audiowide'] leading-none text-transparent ${
      size === 16 ? "text-[16px]" : "text-[14px]"
    }`}
  >
    / {xp}XP
  </span>
);

const GiftIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    className={className}
  >
    <path d="M20 6h-2.18c.11-.31.18-.65.18-1a3 3 0 0 0-5.5-1.65l-.5.67-.5-.68A3 3 0 0 0 6 5c0 .35.07.69.18 1H4a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2zm-5-2a1 1 0 1 1 0 2 1 1 0 0 1 0-2zM9 4a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 11 8.76l1-1.36 1 1.36L14.38 12 16 10.83 13.92 8H20v6z" />
  </svg>
);

interface ClaimButtonStyle {
  className: string;
  label: string;
  disabled: boolean;
}

const getClaimButtonStyle = (
  status: CompanyQuestStatus,
  isClaiming: boolean,
): ClaimButtonStyle => {
  if (status === "completed") {
    return {
      className: `border border-[#d4f754] text-[#1b4b86] ${CLAIM_GRADIENT}`,
      label: "報酬受取",
      disabled: isClaiming,
    };
  }
  if (status === "claimed") {
    return {
      className: "cursor-not-allowed bg-[#002959] text-[#d4f754]/50",
      label: "受取済",
      disabled: true,
    };
  }
  return {
    className: "cursor-not-allowed bg-[#002959] text-[#1b4b86]",
    label: "報酬受取",
    disabled: true,
  };
};

interface QuestItemProps {
  quest: CardQuest;
  isClaiming: boolean;
  /** Trả về số XP nhận được khi thành công, null khi lỗi. */
  onClaim: (companyQuestId: string) => Promise<number | null>;
}

const QuestItem = ({ quest, isClaiming, onClaim }: QuestItemProps) => {
  const button = getClaimButtonStyle(quest.status, isClaiming);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [xpPop, setXpPop] = useState<number | null>(null);

  const handleClick = async () => {
    if (quest.status !== "completed" || !quest.companyQuestId) return;
    const rewardedXp = await onClaim(quest.companyQuestId);
    if (rewardedXp && rewardedXp > 0) {
      fireConfettiAt(buttonRef.current);
      setXpPop(rewardedXp);
    }
  };

  return (
    <div
      data-testid={`quest-item-${quest.id}`}
      className="flex flex-col gap-[10px] border-t border-white/50 py-[20px] first:border-t-0"
    >
      <div className="flex flex-col gap-[2px]">
        <div className="flex items-baseline gap-[8px]">
          <span className="font-['Noto_Sans_JP'] text-[16px] font-bold leading-[1.6] text-white">
            {quest.title}
          </span>
        </div>
        <p className="font-['Noto_Sans_JP'] text-[13px] leading-[1.6] text-white">
          {quest.description}
        </p>
        <p className="font-['Noto_Sans_JP'] text-[12px] leading-[1.6] text-white/80">
          {quest.activePeriodLabel}
        </p>
      </div>

      <div className="flex flex-col gap-[10px] sm:flex-row sm:items-center sm:gap-[15px]">
        <div className="flex items-center gap-[15px] sm:flex-1">
          <div className="h-[16px] flex-1 overflow-hidden rounded-[8px] bg-[#002959]">
            <div
              className={`h-full rounded-[8px] ${PROGRESS_GRADIENT}`}
              style={{ width: `${quest.progress}%` }}
            />
          </div>

          <QuestXp xp={quest.xp} size={16} />

          {/* phần trăm: ẩn trên mobile, hiện từ breakpoint sm trở lên */}
          <div className="hidden w-[70px] shrink-0 items-baseline justify-end gap-[2px] sm:flex">
            <span className="font-['Audiowide'] text-[26px] leading-[1.1] text-[#d4f754]">
              {quest.progress}
            </span>
            <span className="font-['Noto_Sans_JP'] text-[12px] font-bold text-white">
              %
            </span>
          </div>

          <QuestRewardMedal badge={quest.rewardBadge} />
        </div>

        {/* nút claim: chiếm nguyên 1 dòng trên mobile, nằm cùng hàng từ sm trở lên */}
        <div className="relative w-full shrink-0 sm:w-auto">
          {xpPop !== null && (
            <span
              className="pointer-events-none absolute -top-[6px] left-1/2 -translate-x-1/2 -translate-y-full animate-xp-pop whitespace-nowrap font-['Audiowide'] text-[18px] text-[#d4f754] [text-shadow:0_1px_4px_rgba(0,0,0,0.4)]"
              onAnimationEnd={() => setXpPop(null)}
            >
              +{xpPop} XP
            </span>
          )}
          <button
            ref={buttonRef}
            type="button"
            disabled={button.disabled}
            onClick={handleClick}
            className={`flex h-[35px] w-full items-center justify-center gap-[3px] rounded-[6px] px-[10px] py-[7px] sm:w-auto ${button.className}`}
          >
            <GiftIcon className="h-[18px] w-[18px]" />
            <span className="font-['Noto_Sans_JP'] text-[13px] font-bold leading-[1.6]">
              {button.label}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

const QuestCardMessage = ({ children }: { children: React.ReactNode }) => (
  <p className="py-[30px] text-center font-['Noto_Sans_JP'] text-[14px] leading-[1.6] text-white/70">
    {children}
  </p>
);

const CompanyQuestCard = () => {
  const { data, isLoading, isError } = useGetMyQuestsQuery({ type: "season" });
  const [claimQuest, { isLoading: isClaiming }] = useClaimQuestMutation();

  const quests = (data?.data ?? []).map(toCardQuest);

  const handleClaim = async (
    companyQuestId: string,
  ): Promise<number | null> => {
    try {
      const res = await claimQuest({ companyQuestId }).unwrap();
      const rewardedXp = res.data.rewarded_xp;
      message.success(`報酬を受け取りました（+${rewardedXp} XP）`);
      return rewardedXp;
    } catch (error) {
      message.error("報酬の受け取りに失敗しました");
      return null;
    }
  };

  const renderBody = () => {
    if (isLoading) {
      return <QuestCardMessage>読み込み中...</QuestCardMessage>;
    }
    if (isError) {
      return (
        <QuestCardMessage>クエストを取得できませんでした</QuestCardMessage>
      );
    }
    if (quests.length === 0) {
      return <QuestCardMessage>現在のクエストはありません</QuestCardMessage>;
    }
    return (
      <div className="-mr-[6px] max-h-[510px] overflow-y-auto pr-[6px] [scrollbar-color:rgba(255,255,255,0.45)_transparent] [scrollbar-width:thin]">
        <div className="flex flex-col">
          {quests.map((quest) => (
            <QuestItem
              key={quest.id}
              quest={quest}
              isClaiming={isClaiming}
              onClaim={handleClaim}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <CompanyProfileCard className="xl:min-h-[406px]">
      <CompanyProfileCardTitle>今月クエスト</CompanyProfileCardTitle>
      {renderBody()}
    </CompanyProfileCard>
  );
};

export default CompanyQuestCard;
